import { ReactNode } from 'react';

import { Theme, createStyles, makeStyles } from '@material-ui/core';

import { RiChat3Line } from '@remixicon/react';

import { Button } from '@backstage/ui';

import {
  FeedActivityFragment,
  getTypeOrNull,
} from '@spotify/confidence-graphql';
import {
  EmojiReactions,
  useReactions,
} from '@spotify/plugin-confidence-comments-react';

import { ActivityFeedItem } from '../ActivityFeedItem';
import { useActivityFeedItemContext } from '../ActivityFeedItem/context';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
  }),
);

interface FooterWithReactionsProps {
  activity: FeedActivityFragment;
}

const FooterWithReactions = ({ activity }: FooterWithReactionsProps) => {
  const classes = useStyles();
  const { showComments, setShowComments, reactions } =
    useActivityFeedItemContext();

  const commentsCount = reactions.commentCount ?? 0;

  // Extract emoji reactions from the activity's reaction summary
  const reactionSummary = getTypeOrNull(activity.queryReactionSummary)?.summary;

  const { reactedEmojis, toggleReaction, emojiReactions } = useReactions(
    activity.name,
    reactionSummary,
  );

  return (
    <div className={classes.footer}>
      <Button
        variant={commentsCount && commentsCount > 0 ? 'secondary' : 'tertiary'}
        size="small"
        iconStart={<RiChat3Line />}
        onPress={() => setShowComments(!showComments)}
      >
        {commentsCount > 0 ? commentsCount : undefined}
      </Button>
      <EmojiReactions
        emojiReactions={emojiReactions}
        userReactedEmojis={reactedEmojis}
        onToggleReaction={toggleReaction}
      />
    </div>
  );
};

export interface FullActivityFeedItemProps {
  activity: FeedActivityFragment;
  /** Override for activity.title */
  title?: ReactNode;
  /** Custom content for the body */
  children?: ReactNode;
}

export const FullActivityFeedItem = ({
  activity,
  title,
  children,
}: FullActivityFeedItemProps) => {
  const reactionSummary = getTypeOrNull(activity.queryReactionSummary);

  return (
    <ActivityFeedItem.Root
      activity={activity}
      reactions={{ commentCount: reactionSummary?.summary?.commentCount ?? 0 }}
    >
      <ActivityFeedItem.Header />
      <ActivityFeedItem.Content>
        <ActivityFeedItem.Title>
          {title ?? activity.title}
        </ActivityFeedItem.Title>
        <ActivityFeedItem.Body>
          {children ?? activity.body}
        </ActivityFeedItem.Body>
      </ActivityFeedItem.Content>
      <FooterWithReactions activity={activity} />
      <ActivityFeedItem.Comments />
    </ActivityFeedItem.Root>
  );
};
